import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Loader2, PhoneOff, Moon, UserCheck, CircleCheck } from 'lucide-react';
import {
  ResponsiveContainer, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Line, ComposedChart,
} from 'recharts';
import { api, type LlamadasResumen } from '@/lib/api';
import { useChartTheme, dayLabel, Panel, Stat, HeatmapSemanal } from '@/components/aperturas/shared';
import type { LlamadasContext } from './LlamadasLayout';

/** Seconds as something a person reads at a glance: 95 -> "1m 35s". */
export function formatDuracion(segundos: number | null): string {
  if (segundos === null || segundos === undefined) return '—';
  if (segundos < 60) return `${segundos}s`;
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/** '2026-05-26' -> '26 May'. El ISO crudo ocupaba dos líneas en un móvil. */
function diaCorto(iso: string): string {
  const [, mes, dia] = iso.split('-');
  return `${Number(dia)} ${MESES_CORTOS[Number(mes) - 1]}`;
}

/** Smooths the daily series so the trend shows through the day-to-day noise. */
function conMediaMovil(serie: { day: string; total: number }[], ventana = 7) {
  return serie.map((punto, i) => {
    const desde = Math.max(0, i - ventana + 1);
    const trozo = serie.slice(desde, i + 1);
    const media = trozo.reduce((s, p) => s + p.total, 0) / trozo.length;
    return { ...punto, media: Math.round(media * 10) / 10 };
  });
}

/**
 * Si la pantalla es estrecha.
 *
 * Los gráficos de barras horizontales reservan un ancho fijo para las
 * etiquetas del eje. Los 150px que van bien en un portátil se comen dos
 * tercios de un móvil de 375px y dejan las barras sin sitio, así que el ancho
 * tiene que decidirse en JavaScript: no es una cuestión de CSS, es un
 * parámetro que Recharts recibe como número.
 */
function useEsMovil(limite = 640): boolean {
  const [esMovil, setEsMovil] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${limite - 1}px)`).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${limite - 1}px)`);
    const alCambiar = (e: MediaQueryListEvent) => setEsMovil(e.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, [limite]);
  return esMovil;
}

export default function LlamadasMetricasPage() {
  const esMovil = useEsMovil();
  const chart = useChartTheme();
  const { from, to } = useOutletContext<LlamadasContext>();
  const [data, setData] = useState<LlamadasResumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getLlamadasResumen(from, to)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Error cargando datos'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [from, to]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;
  }
  if (error) {
    return <div className="card text-center py-12 text-slate-500">{error}</div>;
  }
  if (!data || data.total === 0) {
    return <div className="card text-center py-12 text-slate-500">No hay llamadas registradas en este periodo.</div>;
  }

  const serie = conMediaMovil(data.serie);
  const { transcripciones } = data;
  const pctTranscritas = transcripciones.total
    ? Math.round((transcripciones.listas / transcripciones.total) * 100)
    : 0;

  // Peak hour across the whole period, so the headline says something concrete
  // instead of making the reader decode the heatmap.
  const porHora = new Map<number, number>();
  for (const c of data.heatmap) porHora.set(c.hour, (porHora.get(c.hour) ?? 0) + c.total);
  const horaPico = [...porHora.entries()].sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-4">
        <Stat label="Llamadas" value={data.total.toLocaleString('es-ES')} hint={`${diaCorto(data.periodo.from)} – ${diaCorto(data.periodo.to)}`} />
        <Stat
          label="Duración mediana"
          value={formatDuracion(data.duracion.mediana)}
          hint={`media ${formatDuracion(data.duracion.media)} · p90 ${formatDuracion(data.duracion.p90)}`}
        />
        <Stat
          label="Hora punta"
          value={horaPico ? `${String(horaPico[0]).padStart(2, '0')}:00` : '—'}
          hint="hora de Madrid"
        />
        <Stat
          label="Teléfonos que repiten"
          value={data.telefonosRecurrentes}
          hint="llamaron más de una vez"
        />
        {/* Impar entre cuatro: en dos columnas quedaría suelta a media fila, así
            que en móvil ocupa el ancho entero y se lee como un cierre. */}
        <div className="col-span-2 lg:col-span-1">
          <Stat
            label="Transcritas"
            value={`${pctTranscritas}%`}
            hint={
              transcripciones.pendientes > 0
                ? `${transcripciones.listas} de ${transcripciones.total} · ${transcripciones.pendientes} en cola`
                : `${transcripciones.listas} de ${transcripciones.total}`
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-4">
        <Panel title="Colgadas enseguida" hint="Duran menos de 10 segundos: alguien que cuelga, no una conversación. Un porcentaje alto apunta a fricción en el menú de voz.">
          <div className="flex items-center gap-3">
            <PhoneOff className="text-slate-400" size={22} />
            <div>
              <p className="text-2xl font-bold text-sereno-dark dark:text-white">{data.colgadas.pct}%</p>
              <p className="text-xs text-slate-500">{data.colgadas.total} llamadas</p>
            </div>
          </div>
        </Panel>
        <Panel title="Fuera de horario" hint="Noches y fines de semana, tomando como horario de oficina de 9 a 18 de lunes a viernes.">
          <div className="flex items-center gap-3">
            <Moon className="text-slate-400" size={22} />
            <div>
              <p className="text-2xl font-bold text-sereno-dark dark:text-white">{data.fueraHorario.pct}%</p>
              <p className="text-xs text-slate-500">{data.fueraHorario.total} llamadas</p>
            </div>
          </div>
        </Panel>
        <Panel title="Huésped identificado" hint="Llamadas cuyo número se reconoce en la base de huéspedes. El resto llama desde un número que no tenemos.">
          <div className="flex items-center gap-3">
            <UserCheck className="text-slate-400" size={22} />
            <div>
              <p className="text-2xl font-bold text-sereno-dark dark:text-white">{data.identificadas.pct}%</p>
              <p className="text-xs text-slate-500">{data.identificadas.total} llamadas</p>
            </div>
          </div>
        </Panel>
      </div>

      {data.analisis.analizadas > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-4">
            <Panel
              title="Se resolvieron"
              hint={`Sobre las ${data.analisis.analizadas} llamadas ya analizadas. Una llamada cuenta como resuelta si el cliente obtuvo lo que buscaba antes de colgar.`}
            >
              <div className="flex items-center gap-3">
                <CircleCheck className="text-slate-400" size={22} />
                <div>
                  <p className="text-2xl font-bold text-sereno-dark dark:text-white">{data.analisis.pctResueltas}%</p>
                  <p className="text-xs text-slate-500">
                    {data.analisis.resueltas} de {data.analisis.analizadas}
                  </p>
                </div>
              </div>
            </Panel>

            <Panel title="Cómo se fueron" hint="Sentimiento del huésped en el conjunto de la llamada, no en un momento suelto.">
              <div className="space-y-1.5">
                {data.analisis.sentimientos.map((s) => {
                  const pct = Math.round((s.total / data.analisis.analizadas) * 100);
                  const color =
                    s.nombre === 'POSITIVE' ? 'bg-emerald-500'
                    : s.nombre === 'NEGATIVE' ? 'bg-rose-500'
                    : s.nombre === 'MIXED' ? 'bg-amber-500' : 'bg-slate-300';
                  const texto =
                    s.nombre === 'POSITIVE' ? 'positivo'
                    : s.nombre === 'NEGATIVE' ? 'negativo'
                    : s.nombre === 'MIXED' ? 'mixto' : 'neutro';
                  return (
                    <div key={s.nombre} className="flex items-center gap-2 text-xs">
                      <span className="w-14 text-slate-500">{texto}</span>
                      <span className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden">
                        <span className={`block h-full ${color}`} style={{ width: `${pct}%` }} />
                      </span>
                      <span className="w-8 text-right tabular-nums text-slate-500">{s.total}</span>
                    </div>
                  );
                })}
              </div>
            </Panel>

            <Panel title="Cobertura del análisis" hint="Solo se analizan las llamadas cuya transcripción está separada por interlocutor. Las del histórico antiguo se van incorporando conforme se rehacen.">
              <div>
                <p className="text-2xl font-bold text-sereno-dark dark:text-white">
                  {Math.round((data.analisis.analizadas / data.total) * 100)}%
                </p>
                <p className="text-xs text-slate-500">{data.analisis.analizadas} de {data.total} llamadas</p>
              </div>
            </Panel>
          </div>

          <Panel
            title="Por qué llaman"
            hint="El motivo lo determina un modelo leyendo la conversación. Las llamadas erróneas son las que se cortan o no llegan a plantear nada."
          >
            <ResponsiveContainer width="100%" height={Math.max(180, data.analisis.categorias.length * 30)}>
              <BarChart data={data.analisis.categorias} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} horizontal={false} />
                <XAxis type="number" stroke={chart.axis} fontSize={11} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="nombre"
                  stroke={chart.axis}
                  fontSize={esMovil ? 10 : 11}
                  width={esMovil ? 92 : 150}
                  tickFormatter={(v: string) => v.replace(/_/g, ' ')}
                />
                <Tooltip cursor={{ fill: chart.cursor }} formatter={(v: any) => [`${v} llamadas`, '']} />
                <Bar dataKey="total" fill={chart.bar} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </>
      )}

      <Panel title="Cuándo llaman" hint="Color según las llamadas de un lunes medio a esa hora, no el total: un periodo con cinco lunes y cuatro martes pintaría el lunes más oscuro sin que nadie llame más.">
        <HeatmapSemanal cells={data.heatmap} unidad="llamadas" />
      </Panel>

      <Panel title="Llamadas por día" hint="La línea es la media de los últimos 7 días, para ver la tendencia por debajo del ruido diario.">
        <ResponsiveContainer width="100%" height={esMovil ? 200 : 260}>
          <ComposedChart data={serie}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
            <XAxis dataKey="day" tickFormatter={dayLabel} stroke={chart.axis} fontSize={11} minTickGap={28} />
            <YAxis stroke={chart.axis} fontSize={11} allowDecimals={false} />
            <Tooltip
              cursor={{ fill: chart.cursor }}
              labelFormatter={(d) => dayLabel(String(d))}
              formatter={(v: any, n: any) => [v, n === 'media' ? 'media 7 días' : 'llamadas']}
            />
            <Area type="monotone" dataKey="total" name="total" stroke={chart.bar} fill={chart.bar} fillOpacity={0.18} />
            <Line type="monotone" dataKey="media" name="media" stroke={chart.palette[1]} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>

      {data.alojamientos.length > 0 && (
        <Panel title="Llamadas por alojamiento" hint="Se atribuye cada llamada al alojamiento de la reserva que cubría ese día. Solo aparecen las llamadas cuyo huésped hemos podido identificar.">
          <ResponsiveContainer width="100%" height={Math.max(200, data.alojamientos.length * 26)}>
            <BarChart data={data.alojamientos} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} horizontal={false} />
              <XAxis type="number" stroke={chart.axis} fontSize={11} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="nombre"
                stroke={chart.axis}
                fontSize={esMovil ? 10 : 11}
                width={esMovil ? 92 : 150}
                // En móvil un apodo largo desplazaría la barra fuera de la pantalla.
                tickFormatter={(v: string) => (esMovil && v.length > 14 ? `${v.slice(0, 13)}…` : v)}
              />
              <Tooltip cursor={{ fill: chart.cursor }} formatter={(v: any) => [`${v} llamadas`, '']} />
              <Bar dataKey="total" fill={chart.bar} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      {data.duracion.sinDato > 0 && (
        <p className="text-xs text-slate-400">
          {data.duracion.sinDato} llamadas sin duración: no se conserva su grabación, así que no entran en las estadísticas de duración.
        </p>
      )}
    </div>
  );
}
